import m, {MithrilComponent} from '@bitstillery/mithril'
import {classes} from '@bitstillery/common/lib/utils'

import type {CmsComponentMeta} from '@bitstillery/common/cms/catalog_meta'

export const bandMeta = {
    tier: 'layout',
    summary: 'Full-width colour region: wraps content on a deep, hue-derived background with an auto-matched light text colour.',
    when_to_use:
        'Wrap a stretch of content (e.g. a `Products` carousel, or a `Section` + `Grid`) to give it a full-bleed coloured backdrop and act as a rhythm break between light sections. Pick the colour with `hue` only — the band locks saturation and lightness so every band stays deep, on-brand and legible, and derives a matching hue-tinted text colour. Nested components that own their own surface (cards, the product carousel) keep their look; the band colours its own field and headings. Not a hero — for a photo-backed band use `HeroBand`.',
    example: `
    <Band border_bottom_type="zigzag" hue={195}>
        <Section title="Why buy through Discover">
            <Paragraph>Every supplier is verified before a single bottle moves.</Paragraph>
        </Section>
    </Band>
    `,
    variants: [
        {
            prop: 'hue',
            label: 'hue={70}',
            jsx: `
            <Band hue={70}>
                <Section title="Why buy through Discover">
                    <Paragraph>Every supplier is verified before a single bottle moves.</Paragraph>
                </Section>
            </Band>
            `,
        },
        {
            prop: 'hue',
            label: 'hue={330}',
            jsx: `
            <Band hue={330}>
                <Section title="Why buy through Discover">
                    <Paragraph>Every supplier is verified before a single bottle moves.</Paragraph>
                </Section>
            </Band>
            `,
        },
    ],
} satisfies CmsComponentMeta

export interface BandAttrs {
    /**
     * Position on the OkLCh colour wheel in degrees (0–360) — the only colour input. The band derives a deep,
     * on-brand background and a matching hue-tinted light text colour from it; chroma and lightness are
     * locked so every band stays within the brand range and legible regardless of hue. e.g. `70` (amber),
     * `195` (teal), `330` (plum).
     */
    hue: number
    /**
     * Bottom edge treatment: `default` (straight) or `zigzag` (sawtooth edge that leads the eye into the
     * following section).
     */
    border_bottom_type?: 'default' | 'zigzag'
}

/**
 * Full-width colour region. The author picks only a `hue`; the band locks saturation/lightness and computes
 * a matching light text colour, so the colour is always on-brand and readable. Wraps its children full-bleed
 * over the colour — distinct from `HeroBand`, which is photo-backed.
 */
export class Band extends MithrilComponent<BandAttrs> {
    view(vnode: m.Vnode<BandAttrs>) {
        const hue = Number.isFinite(vnode.attrs.hue) ? vnode.attrs.hue : 212
        const zigzag = vnode.attrs.border_bottom_type === 'zigzag'
        /** Only the hue is dynamic; band.css owns the locked saturation/lightness and the derived text colour. */
        const style = {'--h-band': `${hue}deg`} as Record<string, string>

        return (
            <section className={classes('c-band', zigzag ? 'zigzag' : null)} style={style}>
                <div className='inner'>{vnode.children}</div>
            </section>
        )
    }
}
